import { useExecutionProperties } from 'src/modules/automations/hooks/executions/use-execution-properties';
import { useMemo } from 'react';
import {
  PiBrain,
  PiCheckCircle,
  PiDatabase,
  PiFlowArrow,
  PiGear,
  PiTarget,
  PiWarningCircle,
} from 'react-icons/pi';
import type { TCEmployeeStepExecutionListResponse } from 'src/api/generated';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { Badge } from 'src/modules/shared/components/atoms/badge';
import { StatusPill } from 'src/modules/shared/components/atoms/status-pill';
import { Text } from 'src/modules/shared/components/atoms/text';
import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import {
  EventTimeline,
  type EventTimelineItem,
} from 'src/modules/shared/components/molecules/event-timeline';
import { ExpandableSection } from 'src/modules/shared/components/molecules/expandable-section';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import {
  organizeTimelineSteps,
  type RawExecutionStep,
  type TimelineStep,
} from 'src/modules/automations/utils/executions/timeline/organize-timeline.util';
import { getToolMetadata } from 'src/modules/automations/utils/employees/employee-tool-presentation.util';
import { getBlockedValidatorDetails } from 'src/modules/automations/components/molecules/employees/executions/execution-timeline/blocked-validator-step.util';
import {
  buildExecutionStepPresentation,
  hasExecutionStepDetails,
} from 'src/modules/automations/utils/executions/execution-step-presentation.util';
import type { ExecutionStepPresentation } from 'src/modules/automations/types/executions/execution-step-presentation.type';

interface ExecutionChronologyProps {
  /** Raw execution steps returned by the API. */
  steps: TCEmployeeStepExecutionListResponse;
}

const getStatusPresentation = (
  status: string,
  labels: { completed: string; failed: string; running: string }
): {
  label: string;
  variant: ComponentVariant;
  tone: EventTimelineItem['tone'];
} => {
  const normalized = status.toLowerCase();
  if (['completed', 'complete', 'success', 'succeeded'].includes(normalized)) {
    return { label: labels.completed, variant: 'success', tone: 'success' };
  }
  if (['failed', 'error'].includes(normalized)) {
    return { label: labels.failed, variant: 'danger', tone: 'danger' };
  }
  return { label: labels.running, variant: 'info', tone: 'default' };
};

/** Chronological execution story using the shared event timeline. */
export const ExecutionChronology: React.FC<ExecutionChronologyProps> = ({
  steps,
}) => {
  const { language, t } = useI18n();
  const properties = useExecutionProperties(steps);
  const items = useMemo(() => {
    const rawSteps: RawExecutionStep[] = steps.flatMap((step) =>
      'activityType' in step
        ? []
        : [
            {
              ...step,
              version: String(step.version),
            },
          ]
    );
    const organized = organizeTimelineSteps(rawSteps);
    const labels = {
      completed: t('automations.aiAgents.executions.status.completed'),
      failed: t('automations.aiAgents.executions.status.failed'),
      running: t('automations.aiAgents.executions.status.running'),
    };

    return organized.map((step, index) => {
      const phase = 'phase' in step.data ? step.data.phase.toLowerCase() : '';
      const presentation = getPhasePresentation(phase, t);
      const stepDetails = buildExecutionStepPresentation(step.data, properties);
      const status = getStatusPresentation(step.data.status, labels);
      const blocked = getBlockedValidatorDetails(step);
      const createdAt = step.data.createdAt;
      const formattedDate = createdAt
        ? new Intl.DateTimeFormat(language, {
            dateStyle: 'medium',
            timeStyle: 'short',
          }).format(new Date(createdAt))
        : '';

      return {
        key: `${step.data.executionId}-${index}`,
        label: presentation.label,
        description: stepDetails.objective ?? presentation.description,
        icon: presentation.icon,
        value: formattedDate,
        valueTooltip: createdAt,
        tone: blocked ? 'warning' : status.tone,
        status: (
          <StatusPill
            label={
              blocked
                ? t('automations.aiAgents.executions.status.blocked')
                : status.label
            }
            variant={blocked ? 'warning' : status.variant}
            appearance="soft"
            size="xs"
          />
        ),
        tags: buildStepTags(step, t),
        content:
          blocked ||
          hasExecutionStepDetails(stepDetails) ||
          step.children.length > 0 ? (
            <div className="space-y-3">
              {blocked ? (
                <Alert
                  variant="warning"
                  appearance="soft"
                  size="sm"
                  icon={<PiWarningCircle className="h-4 w-4" />}
                  message={
                    <div className="space-y-1">
                      <Text size="small" weight="medium">
                        {blocked.reason}
                      </Text>
                      {blocked.affectedTodo ? (
                        <Text size="tiny">{blocked.affectedTodo}</Text>
                      ) : null}
                    </div>
                  }
                />
              ) : null}
              {hasExecutionStepDetails(stepDetails) ? (
                <ExpandableSection
                  title={t('automations.aiAgents.detail.phaseDetails')}
                  isInitiallyExpanded={Boolean(blocked ?? stepDetails.error)}
                  isContentScrollable={false}
                  showScrollHint={false}
                  contentClassName="max-h-none"
                  className="rounded-lg border border-slate-200 bg-white"
                  compactHeaderOnMobile
                >
                  <StepSemanticDetails presentation={stepDetails} />
                </ExpandableSection>
              ) : null}
              {step.children.length > 0 ? (
                <div className="space-y-2">
                  {step.children.map((child, childIndex) => (
                    <TimelineAction
                      key={`${child.data.executionId}-${childIndex}`}
                      step={child}
                      completedLabel={labels.completed}
                      failedLabel={labels.failed}
                      runningLabel={labels.running}
                      detailsLabel={t(
                        'automations.aiAgents.detail.actionDetails'
                      )}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ) : undefined,
      } satisfies EventTimelineItem;
    });
  }, [language, steps, t, properties]);

  return (
    <EventTimeline
      items={items}
      emptyLabel={t('automations.aiAgents.detail.timelineEmptyDescription')}
    />
  );
};

const getPhasePresentation = (
  phase: string,
  t: ReturnType<typeof useI18n>['t']
): { label: string; description: string; icon: React.ReactNode } => {
  switch (phase) {
    case 'fetch-context':
      return {
        label: t('automations.aiAgents.detail.phase.fetchContext'),
        description: t(
          'automations.aiAgents.detail.phase.fetchContextDescription'
        ),
        icon: <PiDatabase className="h-4 w-4" />,
      };
    case 'planner':
      return {
        label: t('automations.aiAgents.detail.phase.planner'),
        description: t('automations.aiAgents.detail.phase.plannerDescription'),
        icon: <PiTarget className="h-4 w-4" />,
      };
    case 'executor':
      return {
        label: t('automations.aiAgents.detail.phase.executor'),
        description: t('automations.aiAgents.detail.phase.executorDescription'),
        icon: <PiFlowArrow className="h-4 w-4" />,
      };
    case 'validator':
      return {
        label: t('automations.aiAgents.detail.phase.validator'),
        description: t(
          'automations.aiAgents.detail.phase.validatorDescription'
        ),
        icon: <PiCheckCircle className="h-4 w-4" />,
      };
    default:
      return {
        label: t('automations.aiAgents.detail.phase.unknown'),
        description: t('automations.aiAgents.detail.phase.unknownDescription'),
        icon: <PiBrain className="h-4 w-4" />,
      };
  }
};

const buildStepTags = (
  step: TimelineStep,
  t: ReturnType<typeof useI18n>['t']
): EventTimelineItem['tags'] => {
  if (!('stepNumber' in step.data)) {
    return undefined;
  }

  const stepNumber = step.data.stepNumber;
  const cycle = Math.floor((stepNumber - 1) / 4) + 1;
  return [
    {
      key: 'cycle',
      label: t('automations.aiAgents.detail.cycle', { cycle }),
    },
    {
      key: 'step',
      label: t('automations.aiAgents.detail.step', { number: stepNumber }),
    },
  ];
};

const TimelineAction: React.FC<{
  step: TimelineStep;
  completedLabel: string;
  failedLabel: string;
  runningLabel: string;
  detailsLabel: string;
}> = ({ step, completedLabel, failedLabel, runningLabel, detailsLabel }) => {
  const { t } = useI18n();
  const properties = useExecutionProperties([step.data]);
  const toolName = 'toolName' in step.data ? step.data.toolName : undefined;
  const metadata = toolName ? getToolMetadata(toolName, t) : undefined;
  const status = getStatusPresentation(step.data.status, {
    completed: completedLabel,
    failed: failedLabel,
    running: runningLabel,
  });
  const presentation = buildExecutionStepPresentation(step.data, properties);
  const hasDetails = hasExecutionStepDetails(presentation);

  return (
    <div className="rounded-lg border border-slate-100 bg-white px-3 py-2">
      <div className="flex items-center gap-3">
        <span className={metadata?.color ?? 'text-slate-500'}>
          {metadata?.icon ?? <PiGear className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <Text
            size="small"
            weight="medium"
            className="truncate text-slate-700"
          >
            {metadata?.displayName ?? toolName}
          </Text>
          {toolName !== undefined ? (
            <Text size="tiny" className="truncate font-mono text-slate-500">
              {toolName}
            </Text>
          ) : null}
        </div>
        <StatusPill
          label={status.label}
          variant={status.variant}
          appearance="soft"
          size="xs"
        />
      </div>
      {hasDetails ? (
        <ExpandableSection
          title={detailsLabel}
          isInitiallyExpanded={Boolean(presentation.error)}
          isContentScrollable={false}
          showScrollHint={false}
          contentClassName="max-h-none"
          className="mt-2 border-t border-slate-100"
          compactHeaderOnMobile
        >
          <StepSemanticDetails presentation={presentation} />
        </ExpandableSection>
      ) : null}
    </div>
  );
};

const SemanticValue: React.FC<{
  label: string;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <div className="grid gap-0.5 sm:grid-cols-[10rem_1fr] sm:gap-3">
    <Text size="tiny" weight="medium" className="text-slate-500">
      {label}
    </Text>
    <div className="min-w-0 text-sm text-slate-700">{value}</div>
  </div>
);

const StepSemanticDetails: React.FC<{
  presentation: ExecutionStepPresentation;
}> = ({ presentation }) => (
  <div className="space-y-2 pb-2">
    <PhaseSemanticDetails presentation={presentation} />
    <ValidatorSemanticDetails presentation={presentation} />
    <ContextSemanticDetails presentation={presentation} />
    <ToolSemanticDetails presentation={presentation} />
    {presentation.error ? (
      <Alert
        variant="danger"
        appearance="soft"
        size="sm"
        message={presentation.error}
      />
    ) : null}
  </div>
);

const PhaseSemanticDetails: React.FC<{
  presentation: ExecutionStepPresentation;
}> = ({ presentation }) => {
  const { t } = useI18n();

  return (
    <>
      {presentation.objective ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.objective')}
          value={presentation.objective}
        />
      ) : null}
      {presentation.todos.length > 0 ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.plannedTasks')}
          value={
            <ol className="list-decimal space-y-1 pl-4">
              {presentation.todos.map((todo, index) => (
                <li key={`${todo}-${index}`}>{todo}</li>
              ))}
            </ol>
          }
        />
      ) : null}
      {presentation.goalAchieved !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.result')}
          value={
            <Badge
              variant={presentation.goalAchieved ? 'success' : 'danger'}
              appearance="soft"
              size="xs"
            >
              {presentation.goalAchieved
                ? t('automations.aiAgents.detail.goalAchieved')
                : t('automations.aiAgents.detail.goalNotAchieved')}
            </Badge>
          }
        />
      ) : null}
      {presentation.reason ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.reason')}
          value={presentation.reason}
        />
      ) : null}
      {presentation.executedToolsCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.executedTools')}
          value={presentation.executedToolsCount}
        />
      ) : null}
      {presentation.duration ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.duration')}
          value={presentation.duration}
        />
      ) : null}
    </>
  );
};

const ValidatorSemanticDetails: React.FC<{
  presentation: ExecutionStepPresentation;
}> = ({ presentation }) => {
  const { t } = useI18n();

  return (
    <>
      {presentation.validatorAction ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.validatorAction')}
          value={translateValidatorAction(presentation.validatorAction, t)}
        />
      ) : null}
      {presentation.affectedTodo ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.affectedTodo')}
          value={presentation.affectedTodo}
        />
      ) : null}
      {presentation.nextTodo ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.nextTodo')}
          value={presentation.nextTodo}
        />
      ) : null}
      {presentation.remainingTodosCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.remainingTodos')}
          value={presentation.remainingTodosCount}
        />
      ) : null}
    </>
  );
};

const ContextSemanticDetails: React.FC<{
  presentation: ExecutionStepPresentation;
}> = ({ presentation }) => {
  const { t } = useI18n();

  return (
    <>
      {presentation.contextFacts.length > 0 ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.contextFacts')}
          value={presentation.contextFacts.join(' · ')}
        />
      ) : null}
      {presentation.contextMessagesCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.messages')}
          value={presentation.contextMessagesCount}
        />
      ) : null}
      {presentation.contextCallsCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.calls')}
          value={presentation.contextCallsCount}
        />
      ) : null}
    </>
  );
};

const ToolSemanticDetails: React.FC<{
  presentation: ExecutionStepPresentation;
}> = ({ presentation }) => {
  const { t } = useI18n();

  return (
    <>
      {presentation.searchQuery ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.searchQuery')}
          value={presentation.searchQuery}
        />
      ) : null}
      {presentation.resultCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.resultsCount')}
          value={presentation.resultCount}
        />
      ) : null}
      {presentation.sentMessages.length > 0 ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.sentMessages')}
          value={
            <div className="space-y-1">
              {presentation.sentMessages.map((message, index) => (
                <div
                  key={`${message}-${index}`}
                  className="rounded-md bg-slate-50 px-2 py-1.5 whitespace-pre-wrap"
                >
                  {message}
                </div>
              ))}
            </div>
          }
        />
      ) : null}
      {presentation.attemptedMessagesCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.attemptedMessages')}
          value={presentation.attemptedMessagesCount}
        />
      ) : null}
      {presentation.failedMessagesCount !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.failedMessages')}
          value={presentation.failedMessagesCount}
        />
      ) : null}
      {presentation.updatedFields.length > 0 ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.updatedFields')}
          value={presentation.updatedFields.join(', ')}
        />
      ) : null}
      {presentation.taskObjective ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.task')}
          value={presentation.taskObjective}
        />
      ) : null}
      {presentation.taskStatus ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.taskStatus')}
          value={presentation.taskStatus}
        />
      ) : null}
      {presentation.taskDueDate ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.taskDueDate')}
          value={presentation.taskDueDate}
        />
      ) : null}
      {presentation.propertyId !== undefined ? (
        <SemanticValue
          label={t('automations.aiAgents.detail.selectedProperty')}
          value={
            presentation.propertyId === '' ? (
              t('automations.aiAgents.detail.propertyCleared')
            ) : (
              <PropertyCode
                displayCode={presentation.propertyDisplayCode}
                fallback={presentation.propertyId}
                internalPrefix=""
              />
            )
          }
        />
      ) : null}
    </>
  );
};

const translateValidatorAction = (
  action: string,
  t: ReturnType<typeof useI18n>['t']
): string => {
  switch (action) {
    case 'completed-todo':
      return t('automations.aiAgents.detail.validator.completedTodo');
    case 'completed-objective':
      return t('automations.aiAgents.detail.validator.completedObjective');
    case 'cancelled-todo':
      return t('automations.aiAgents.detail.validator.cancelledTodo');
    case 'cancelled-objective':
      return t('automations.aiAgents.detail.validator.cancelledObjective');
    case 'kept-in-progress':
      return t('automations.aiAgents.detail.validator.keptInProgress');
    case 'blocked-todo':
      return t('automations.aiAgents.detail.validator.blockedTodo');
    case 'blocked-objective':
      return t('automations.aiAgents.detail.validator.blockedObjective');
    default:
      return action;
  }
};
